import {useQuery} from "@apollo/client";
import {useContext, useEffect, useState} from "react";
import {FiPlus} from "react-icons/fi";

import {ActionBarWrapper} from "@/components/actionBarWrapper/ActionBarWrapper.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {TabContentContainer} from "@/components/layout/TabContentContainer.tsx";
import {TabSection} from "@/components/layout/TabContentContainer.types.ts";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {useTable} from "@/lib/hooks/useTable.ts";
import {useTabs} from "@/lib/hooks/useTabs.tsx";
import {
  GQL_INCIDENTS_COUNTS,
  GQL_STATUSPAGE_INCIDENTS,
  StatusPageIncidentsResponse,
} from "@/lib/queries/statuspages.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {Incident, IncidentCounts, IncidentSortBy, IncidentTime, IncidentType} from "@/lib/types/statuspages.ts";
import {IncidentCreateAction} from "@/pages/incidents/actions/IncidentCreateAction.tsx";
import {StatusPageContext} from "@/pages/statuspages/statuspages-manage/ManageStatusPageTabContainer.utils.tsx";
import {BulkActionsButtonBar} from "@/pages/statuspages/tabs/incidents/actions/BulkActionsButtonBar.tsx";
import {IncidentsSortMenu} from "@/pages/statuspages/tabs/incidents/IncidentsSortMenu/IncidentsSortMenu.tsx";
import {IncidentsTable} from "@/pages/statuspages/tabs/incidents/IncidentsTable.tsx";

interface StatusPageIncidentTabProps {
  incidentType: IncidentType;
}

const urlTabParam = "incidents_tab";

export const StatusPageIncidentsTab = ({incidentType}: StatusPageIncidentTabProps) => {
  const {statusPage} = useContext(StatusPageContext);
  const statusPageID = statusPage.id;
  const {tab, changeTab} = useTabs({
    defaultTab: IncidentTime.ACTIVE,
    urlTabParam: urlTabParam,
    tabList: ["ACTIVE", "RESOLVED", "UPCOMING"],
    preserveSearchParams: ["tab"],
  });

  const [sortBy, setSortBy] = useState<IncidentSortBy>(IncidentSortBy.MOST_RECENTLY_CREATED);
  const {search, searchDebounced, searchChanged, discardPendingSearch, pagination, setPagination} = useTable({
    clearKeysOnSearchChange: ["incident_id"],
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const totalsResponse = useQuery<IncidentCounts>(GQL_INCIDENTS_COUNTS, {
    fetchPolicy: "network-only",
    variables: {
      id: statusPageID,
      incidentType: incidentType,
    },
  });
  const {data, previousData, loading} = useQuery<StatusPageIncidentsResponse>(GQL_STATUSPAGE_INCIDENTS, {
    variables: {
      id: statusPageID,
      incidentType: incidentType,
      incidentTime: tab,
      query: searchDebounced,
      sortBy: sortBy,
      ...pagination,
    },
  });

  useEffect(() => {
    setRowSelection({});
  }, [data]);

  let items: Incident[] = [],
    incidentTotal: number;
  if (data?.response.status === ResponseStatus.SUCCESS && data.response.data) {
    items = data.response.data;
    incidentTotal = data.response.total || 0;
  } else if (previousData?.response.status === ResponseStatus.SUCCESS && previousData.response.data) {
    items = previousData.response.data;
    incidentTotal = previousData.response.total || 0;
  } else {
    incidentTotal = 0;
  }
  const sections: Record<string, TabSection> = {
    [IncidentTime.ACTIVE]: {
      title: "Active",
      id: IncidentTime.ACTIVE,
      badgeText: totalsResponse.data?.ACTIVE.total,
      isVisible: true,
    },
    [IncidentTime.RESOLVED]: {
      title: "Resolved",
      id: IncidentTime.RESOLVED,
      badgeText: totalsResponse?.data?.RESOLVED.total,
      isVisible: true,
    },
    [IncidentTime.UPCOMING]: {
      title: "Upcoming",
      id: IncidentTime.UPCOMING,
      badgeText: totalsResponse?.data?.UPCOMING.total,
      isVisible: incidentType == IncidentType.SCHEDULED_MAINTENANCE,
    },
  };
  const {user} = useCurrentUserContext();
  return (
    <>
      <ActionBarWrapper
        left={
          <>
            <SearchInput
              variant="tableControlBar"
              value={search}
              onChange={(pattern) => {
                setRowSelection({});
                searchChanged(pattern);
              }}
            />
            <IncidentsSortMenu setSortBy={setSortBy} sortBy={sortBy} />
          </>
        }
        right={
          <>
            <IncidentCreateAction
              incidentType={incidentType}
              statusPageId={statusPageID}
              className="text-nowrap"
              disableStatusPageSelect={true}
            >
              <Icon icon={FiPlus} />
              {incidentType === IncidentType.INCIDENT ? "Create Incident" : "Schedule Maintenance"}
            </IncidentCreateAction>
          </>
        }
      />
      <TabContentContainer
        sections={sections}
        tab={tab}
        setTab={(t) => {
          setRowSelection({});
          discardPendingSearch();
          changeTab(t as IncidentTime);
        }}
        title=""
        variant="secondary"
      >
        <IncidentsTable
          key={`${incidentType} - ${tab}`}
          statusPageID={statusPageID}
          incidents={items}
          incidentType={incidentType}
          incidentTime={tab as IncidentTime}
          isFiltered={!!searchDebounced}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          actionBar={
            user?.has_write_access && (
              <BulkActionsButtonBar
                selectedItemsIds={Object.keys(rowSelection).map((id) => parseInt(id))}
                onSuccess={() => setRowSelection({})}
              />
            )
          }
          loading={loading}
          pagination={pagination}
          totalRows={incidentTotal}
          updatePagination={setPagination}
          paginationData={items}
        />
      </TabContentContainer>
    </>
  );
};
